import React, { useState, useEffect, useRef, useContext } from 'react';
import ListLoadError from '../../components/ListLoadError';
import EntityScopeNotice from '../../components/Invoicing/EntityScopeNotice';
import FinancialsEmptyState from '../../components/Invoicing/FinancialsEmptyState';
import { extractApiError } from '../../utils/errorMessages';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoiceDollar, faPlus, faEye, faCheckCircle, faClock, faExclamationTriangle, faArrowLeft, faSync, faEdit, faTrash, faClipboardList, faTimesCircle, faDownload, faFilePdf, faProjectDiagram, faBan, faBell, faMoneyBillWave } from '@fortawesome/free-solid-svg-icons';
import Layout from '../../components/Layout';
import logo from '../../assets/images/IMT_logo.png';
import Footer from '../../components/ui/Footer';
import invoicingService, {
  deliveryScopedListNotice, deliveryScopedEmptyNotice,
  deliveryScopedListHeading, DELIVERY_SCOPED_NOTICE_STYLE,
} from '../../services/invoicingService';
import { AuthContext } from '../../context/AuthContext';
import { hasFinancialAccess, isDeliveryScopedInvoiceReader } from '../../utils/financialAccess';
import { isRestrictedCustomerTrader } from '../../utils/customerTraderAccess';
import InvoiceAuditModal from '../../components/Invoicing/InvoiceAuditModal';
import InvoiceSearchFilter from '../../components/Invoicing/InvoiceSearchFilter';
import InvoiceRelationshipModal from '../../components/Invoicing/InvoiceRelationshipModal';
import PdfPreviewModal from '../../components/shared/PdfPreviewModal';
import VoidInvoiceModal from './VoidInvoiceModal';
import PaymentRemindersModal from './PaymentRemindersModal';
import RecordPaymentModal from './RecordPaymentModal';
import './OutgoingInvoice.css';
import SortableTh from '../../components/shared/SortableTh';
import useTableSort from '../../components/shared/useTableSort';
import useSavedRowFlash from '../../components/shared/useSavedRowFlash';
import SavedRowBanner from '../../components/shared/SavedRowBanner';
import { OUTGOING_INVOICE_COLUMNS, outgoingPaymentStatus, partPaidSummary } from './listColumns';

/* What this screen actually shows. The delivery-scoped banner used to be
 * hard-coded "Showing your deliveries' invoices." on all four list screens,
 * so an operator on this one was told the wrong noun about the very list they
 * were being asked to trust. */
const SCOPED_DOCUMENT_PLURAL = 'outgoing invoices';

// Statuses at which an issued invoice is "live" and can be voided
const VOIDABLE_STATUSES = ['APPROVED', 'SENT', 'PAID', 'OVERDUE'];
// A payment can be recorded against a live, unsettled invoice. DRAFT has no
// agreed sum yet; PAID already has one; the rest are terminal.
const PAYABLE_STATUSES = ['APPROVED', 'SENT', 'OVERDUE'];

/**
 * Outgoing Invoice List Page
 * 
 * Features:
 * - List all outgoing invoices
 * - Filter by status, entity, partner, date range
 * - Search by invoice number or partner name
 * - Create new invoice button
 * - View invoice details
 */

const OutgoingInvoiceList = () => {
  const history = useHistory();
  // WHICH READER IS THIS? See IncomingInvoiceList for the full note — the same
  // two readers share all four invoice lists since 2026-09-07. A finance user
  // reads their whole entity book; a trade-ops operator reads only the invoices
  // raised against deliveries they can already open, because
  // invoicing.delivery_scoped_access clamps the rows. This flag keeps the SCREEN
  // honest about that: anything below that the server would refuse is hidden
  // rather than left to fail into an empty panel.
  const { user } = useContext(AuthContext);
  const scopedReader = isDeliveryScopedInvoiceReader(user);
  // A customer trader is an external counterparty carrying has_bunkers_access,
  // so the route lets them in and the server then refuses them. Refuse here too
  // rather than ask on their behalf and draw a bare table.
  const barredTrader = Boolean(user) && isRestrictedCustomerTrader(user) && !hasFinancialAccess(user);
  const [invoices, setInvoices] = useState([]);
  // Column sorting — comparators live in utils/tableSort, column kinds in
  // ./listColumns, so all six invoicing lists sort by the same rules.
  const { sort, onSort, applySort } = useTableSort(OUTGOING_INVOICE_COLUMNS);
  const [loading, setLoading] = useState(true);
  // Refusal or failure text for the banner. Without it a 403 renders as
  // an empty table, which reads as "you have none" rather than "we could
  // not ask".
  const [loadError, setLoadError] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  
  // Search and filter state
  const [searchResults, setSearchResults] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  
  // Audit modal state
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [selectedInvoiceForAudit, setSelectedInvoiceForAudit] = useState(null);
  
  // Relationship modal state
  const [showRelationshipModal, setShowRelationshipModal] = useState(false);
  const [selectedInvoiceForRelationship, setSelectedInvoiceForRelationship] = useState(null);

  // PDF preview state - status-agnostic preview to check formatting
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewFileName, setPreviewFileName] = useState('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Void modal state
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [invoiceToVoid, setInvoiceToVoid] = useState(null);
  const [isVoiding, setIsVoiding] = useState(false);

  // Payment reminders lightbox state. remindersRefreshToken is bumped whenever
  // the underlying invoices change (void / delete) so the open lightbox
  // refetches instead of showing a row that no longer exists.
  const [showRemindersModal, setShowRemindersModal] = useState(false);
  const [remindersRefreshToken, setRemindersRefreshToken] = useState(0);

  // Component-level flag to prevent double load (resets on unmount)
  const hasLoadedRef = useRef(false);
  
  useEffect(() => {
    // `barredTrader` is false while the profile is still resolving, so re-run on
    // it: the refusal must land the moment we know who is asking.
    if (barredTrader) {
      setInvoices([]);
      loadInvoices();   // takes the refusal branch, makes no request
      return;
    }
    // Load invoices only once on initial mount
    if (!isSearchMode && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchMode, barredTrader]);
  
  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const loadInvoices = async () => {
    // An external counterparty asking for our sales ledger is not a load
    // failure to retry, it is an answer.
    if (barredTrader) {
      setLoading(false);
      setLoadError(
        'Outgoing invoices are internal to IMT. Your account is a customer '
        + 'trader account, which can see its own deliveries but not our invoice '
        + 'book. Contact your IMT representative if you need an invoice.'
      );
      return;
    }
    try {
      setLoading(true);
      setLoadError(null);
      const data = await invoicingService.fetchOutgoingInvoices({});
      setInvoices(data.results || data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading invoices:', error);
      setLoadError(extractApiError(error, 'The invoices could not be loaded.'));
      setLoading(false);
    }
  };

  const handleSearchResults = (results) => {
    console.log('handleSearchResults called with', results.length, 'results');
    setSearchResults(results);
    
    // Enter search mode if we have results or active filters
    if (results.length > 0 || Object.keys(activeFilters).some(key => activeFilters[key])) {
      console.log('ENTERING SEARCH MODE - we have results or filters');
      setIsSearchMode(true);
    } else {
      console.log('NO RESULTS AND NO FILTERS - staying in search mode but showing empty');
      // Keep in search mode but show empty results
      setIsSearchMode(true);
    }
  };

  const handleFiltersChange = (filters) => {
    setActiveFilters(filters);
    // Update search mode based on filters
    const hasFilters = Object.keys(filters).some(key => filters[key]);
    if (hasFilters) {
      setIsSearchMode(true);
    }
  };

  const handleSearchStateChange = (searching) => {
    setIsSearching(searching);
  };

  const handleClearSearch = () => {
    console.log('handleClearSearch() CALLED - EXITING SEARCH MODE AND LOADING ALL ITEMS');
    setIsSearchMode(false);
    setSearchResults([]);
    setActiveFilters({});
    loadInvoices();
  };
  
  const getStatusIcon = (status) => {
    // No icons displayed for any status
    return null;
  };
  
  const formatCurrency = (amount, currency) => {
    if (!amount || isNaN(amount)) return '0.00';
    return parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  
  const formatReportingMonth = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${month} ${year}`;
  };
  
  // One derivation for the badge AND the column sort — if they drifted apart,
  // the list would sort by something other than what it shows.
  const getPaymentStatus = (invoice) => {
    const state = outgoingPaymentStatus(invoice);
    return (
      <span
        className={`payment-badge ${state.toLowerCase().replace(/\s+/g, '-')}`}
        title={state === 'Partially Paid' ? partPaidSummary(invoice) : undefined}
      >
        {state}
      </span>
    );
  };
  
  const toggleDropdown = (invoiceId) => {
    setActiveDropdown(activeDropdown === invoiceId ? null : invoiceId);
  };
  
  const handleView = (invoice, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    history.push(`/invoicing/outgoing/${invoice.id}`);
  };

  const handleEdit = (invoice, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    history.push(`/invoicing/outgoing/edit/${invoice.id}`);
  };

  const handleDelete = async (invoice, e) => {
    e.stopPropagation();
    setActiveDropdown(null);

    if (window.confirm(`Are you sure you want to delete invoice ${invoice.invoice_number}?`)) {
      try {
        await invoicingService.deleteOutgoingInvoice(invoice.id);
        setRemindersRefreshToken(token => token + 1);
        loadInvoices();
      } catch (error) {
        console.error('Error deleting invoice:', error);
        alert('Failed to delete invoice. Please try again.');
      }
    }
  };

  // Record Payment lightbox. Recording a payment writes the date, amount and
  // bank reference, and the status follows — which is what stops the list
  // showing PAID beside PENDING.
  const [paymentInvoice, setPaymentInvoice] = useState(null);

  const handleRecordPayment = (invoice, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    setPaymentInvoice(invoice);
  };

  const handlePaymentRecorded = async () => {
    await loadInvoices();
  };

  const handleVoid = (invoice, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    setInvoiceToVoid(invoice);
    setShowVoidModal(true);
  };

  const handleVoidCancel = () => {
    if (isVoiding) return;
    setShowVoidModal(false);
    setInvoiceToVoid(null);
  };

  const handleVoidConfirm = async () => {
    if (!invoiceToVoid) return;
    setIsVoiding(true);
    try {
      const result = await invoicingService.voidOutgoingInvoice(invoiceToVoid.id);
      setShowVoidModal(false);
      setInvoiceToVoid(null);
      alert(result?.message || 'Invoice voided and credit note created.');
      setRemindersRefreshToken(token => token + 1);
      loadInvoices();
    } catch (error) {
      console.error('Error voiding invoice:', error);
      alert(extractApiError(error, 'Failed to void invoice. Please try again.'));
    } finally {
      setIsVoiding(false);
    }
  };
  
  const handleDownloadPDF = async (invoice, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    
    if (invoice.status !== 'APPROVED') {
      alert('Only approved invoices can be downloaded.');
      return;
    }
    
    try {
      const blob = await invoicingService.downloadOutgoingInvoicePDF(invoice.id);
      const isCopy = blob.documentType === 'copy';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice${isCopy ? '_Copy' : ''}_${invoice.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      // The Original is now spent: relabel this row's button immediately rather
      // than waiting for the next list refresh, so a second press is honest
      // about what it will hand over.
      setInvoices((prev) => prev.map((row) => (
        row.id === invoice.id ? { ...row, original_issued: true } : row
      )));
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    }
  };

  const handlePreviewPDF = async (invoice, e) => {
    e.stopPropagation();
    setActiveDropdown(null);

    setIsLoadingPreview(true);
    try {
      const blob = await invoicingService.downloadOutgoingInvoicePDF(invoice.id, { preview: true });
      const url = window.URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
      setPreviewFileName(`Invoice_${invoice.invoice_number}.pdf`);
      setShowPdfPreview(true);
    } catch (error) {
      console.error('Error loading PDF preview:', error);
      alert('Failed to load PDF preview. Please try again.');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleClosePdfPreview = () => {
    setShowPdfPreview(false);
    if (previewPdfUrl) {
      window.URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl(null);
    }
    setPreviewFileName('');
  };
  
  const handleViewGlobalAudit = () => {
    setSelectedInvoiceForAudit(null);
    setShowAuditModal(true);
  };
  
  const handleViewInvoiceAudit = (invoice, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    setSelectedInvoiceForAudit(invoice);
    setShowAuditModal(true);
  };
  
  const handleCloseAuditModal = () => {
    setShowAuditModal(false);
    setSelectedInvoiceForAudit(null);
  };
  
  const handleViewRelationships = (invoice, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    setSelectedInvoiceForRelationship(invoice);
    setShowRelationshipModal(true);
  };
  
  // Sorted wherever the rows came from — the loaded list OR the search
  // results — so the table never behaves differently in the two modes.
  const visibleRows = applySort(isSearchMode ? searchResults : invoices);
  // Point the user at the row they just saved. The creators hand the id
  // over as route state; without it the toast names a row this table
  // gives no way to find (ordered by document date, not by recency).
  const { savedId, rowRef } = useSavedRowFlash({ sort, isSearchMode, activeFilters });
  const savedRowClass = (id) => (savedId !== null && String(id) === String(savedId) ? 'row-just-saved' : '');
  // The banner speaks only about a row that is actually on this page. If the
  // saved row was filtered out or fell beyond the fetched page, saying "see
  // below" would point at nothing -- the toast stands alone instead.
  // A DRAFT has no number yet, so the label falls back to generic wording
  // rather than printing 'undefined' at the user.
  const savedRow = savedId === null
    ? null
    : visibleRows.find(r => String(r.id) === String(savedId)) || null;
  const savedRowLabel = savedRow?.invoice_number || null;

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="invoice-page-container outgoing-invoice-screen">
        {/* Header */}
        <div className="hms-screen-header">
          {/* Back Button */}
          {/* The Invoicing dashboard aggregates the WHOLE book and stays
              finance-only, so sending an operator there walks them straight back
              into the permission bounce this change exists to remove. */}
          <button
            className="invoice-header-back-button"
            onClick={() => history.push(scopedReader ? '/dashboard' : '/invoicing')}
            title={scopedReader ? 'Back to Dashboard' : 'Back to Invoicing Dashboard'}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>

          <div className="header-logo">
            <img src={logo} alt="IMT Bergbau AG" className="header-logo-img" />
            <div className="header-title-content">
              <h1 className="hms-screen-title">OUTGOING INVOICES</h1>
              <p className="invoice-subtitle">Customer billing and accounts receivable</p>
            </div>
          </div>
          
          <div className="header-actions">
            {/* Payment Reminders reads `payment-reminders` and then SENDS mail;
                Audit Center reads `audit_logs`, a history of the whole book.
                Neither is in the delivery-scoped whitelist, so for an operator
                both could only ever 403 — and a refusal in those modals renders
                as "nothing due" / "no entries", which is a lie about money.
                Hidden for that reader, untouched for Finance. */}
            {!scopedReader && (
              <>
                <button className="btn-secondary" onClick={() => setShowRemindersModal(true)}>
                  <FontAwesomeIcon icon={faBell} /> Payment Reminders
                </button>

                <button className="btn-secondary" onClick={handleViewGlobalAudit}>
                  <FontAwesomeIcon icon={faClipboardList} /> Audit Center
                </button>
              </>
            )}

            {!barredTrader && (
              <button className="btn-primary" onClick={() => history.push('/invoicing/outgoing/create')}>
                <FontAwesomeIcon icon={faPlus} /> Create New Invoice
              </button>
            )}
          </div>
        </div>
        
        {/* The search bar drives the SAME `list` action with query params, which
            the scoped tier is allowed and the server clamps identically — so it
            works for an operator and stays mounted. Not rendered at all for a
            barred customer trader, who has no list to search — and not merely
            `hidden`, because `.search-filter-controls` sets `display: flex`, which
            wins over that attribute. */}
        {/* Search and Filter Controls */}
        {!barredTrader && (
        <div className="search-filter-controls">
          <div className="search-filter-wrapper">
            <InvoiceSearchFilter
              onSearchResults={handleSearchResults}
              onFiltersChange={handleFiltersChange}
              isSearching={isSearching}
              onSearchStateChange={handleSearchStateChange}
              onClearSearch={handleClearSearch}
              invoiceType="outgoing"
            />
          </div>
          
          <button 
            className="reload-button"
            onClick={loadInvoices}
            title="Reload invoices"
          >
            <FontAwesomeIcon icon={faSync} />
          </button>
        </div>
        )}
        
        {/* Invoice List */}
        <div className="invoice-list-container">
          <ListLoadError message={loadError} onRetry={loadInvoices} />
          {/* Says which entity's book this list is showing. Without it a
              narrowed list reads as "nothing has been raised" — the fault
              reported on 2026-09-03.
              An operator is narrowed by DELIVERY, not by entity (the scoped tier
              skips entity scoping on purpose), so the entity sentence would be a
              plain untruth for them. They get the rule that is actually in
              force. */}
          {scopedReader ? (
            <div role="status" style={DELIVERY_SCOPED_NOTICE_STYLE}>
              <strong style={{ marginRight: '0.4rem' }}>
                {deliveryScopedListHeading(SCOPED_DOCUMENT_PLURAL)}
              </strong>
              {deliveryScopedListNotice(SCOPED_DOCUMENT_PLURAL)}
            </div>
          ) : (
            <EntityScopeNotice emptyResult={!loading && visibleRows.length === 0} />
          )}
          <SavedRowBanner savedId={savedRow ? savedId : null} label={savedRowLabel} />
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading invoices...</p>
            </div>
          ) : (
            <table className="invoice-list-table">
              <thead>
                <tr>
                  {OUTGOING_INVOICE_COLUMNS.map((column) => (
                    <SortableTh key={column.key} column={column} sort={sort} onSort={onSort} />
                  ))}
                  {/* Actions holds buttons, not data — nothing to sort by. */}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.length === 0 ? (
                  <tr>
                    <td colSpan={14} className="empty-state-cell">
                      <div className="empty-state-container">
                        <div className="empty-state-icon">
                          <FontAwesomeIcon icon={faFileInvoiceDollar} />
                        </div>
                        {/* FinancialsEmptyState explains an ENTITY narrowing. An
                            operator is narrowed by delivery instead, so it would name
                            the wrong reason in the largest text on the screen. */}
                        {scopedReader ? (
                          <div className="empty-state-text">
                            <h3>Nothing on your deliveries yet</h3>
                            <p>{deliveryScopedEmptyNotice(SCOPED_DOCUMENT_PLURAL)}</p>
                          </div>
                        ) : (
                          <FinancialsEmptyState
                            label="invoices"
                            heading="No Invoices Yet"
                            blurb="Outgoing invoices will appear here"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  visibleRows.map(invoice => (
                    <tr 
                      key={invoice.id} 
                      ref={rowRef(invoice.id)}
                      className={[invoice.is_overdue_display ? 'row-overdue' : '', savedRowClass(invoice.id)].filter(Boolean).join(' ')}
                      onClick={() => history.push(`/invoicing/outgoing/${invoice.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="invoice-number-cell">{invoice.invoice_number || (invoice.status === 'DRAFT' ? 'DRAFT' : 'N/A')}</td>
                      <td style={{ fontSize: '0.9em', color: invoice.source_proforma_number ? '#7B1FA2' : '#999' }}>
                        {invoice.source_proforma_number || '-'}
                      </td>
                      <td>{invoice.hms_entity_name || 'N/A'}</td>
                      <td>{invoice.partner_name || 'N/A'}</td>
                      <td>{invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'N/A'}</td>
                      <td>{invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}</td>
                      <td>{formatReportingMonth(invoice.reporting_month)}</td>
                      <td>{invoice.currency || 'USD'}</td>
                      <td className="amount-cell">{formatCurrency(invoice.net_amount, invoice.currency)}</td>
                      <td className="amount-cell">{formatCurrency(invoice.vat_amount, invoice.currency)}</td>
                      <td className="amount-cell">{formatCurrency(invoice.gross_amount, invoice.currency)}</td>
                      <td className="status-cell">
                        {getStatusIcon(invoice.status)}
                        <span className={`status-badge ${invoice.status.toLowerCase()}`}>
                          {invoice.status_display}
                          {invoice.has_adjustments && (
                            <span className="adjustment-notes-hover-container">
                              <FontAwesomeIcon 
                                icon={faExclamationTriangle} 
                                className="adjustment-icon-indicator"
                                title="This invoice has applied adjustments"
                              />
                              <div className="adjustment-notes-dropdown">
                                <div className="adjustment-notes-dropdown-header">
                                  Applied Adjustments
                                </div>
                                {invoice.total_credits_applied > 0 && (
                                  <div className="adjustment-note-item credit">
                                    <span className="adjustment-note-label">Credits Applied:</span>
                                    <span className="adjustment-note-amount">{formatCurrency(invoice.total_credits_applied, invoice.currency)}</span>
                                  </div>
                                )}
                                <div className="adjustment-notes-dropdown-footer">
                                  Original: {formatCurrency(invoice.gross_amount, invoice.currency)} | 
                                  Adjusted: {formatCurrency(invoice.gross_amount - (invoice.total_credits_applied || 0), invoice.currency)}
                                </div>
                              </div>
                            </span>
                          )}
                        </span>
                      </td>
                      <td>
                        {getPaymentStatus(invoice)}
                      </td>
                      <td className="actions-cell">
                        <div 
                          className={`action-dropdown ${activeDropdown === invoice.id ? 'dropdown-open' : ''}`}
                          onClick={(e) => e.stopPropagation()}
                          ref={activeDropdown === invoice.id ? dropdownRef : null}
                        >
                          <button 
                            className="gear-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(invoice.id);
                            }}
                          >
                            Actions
                          </button>
                        {activeDropdown === invoice.id && (
                          <div className="dropdown-menu">
                            {/* View option always available */}
                            <button onClick={(e) => handleView(invoice, e)}>
                              <FontAwesomeIcon icon={faEye} /> View
                            </button>
                            
                            {/* get_relationships is NOT in the delivery-scoped
                                whitelist, so this could only 403 for an operator
                                and the modal would draw the refusal as an empty
                                graph. */}
                            {!scopedReader && (
                              <button onClick={(e) => handleViewRelationships(invoice, e)}>
                                <FontAwesomeIcon icon={faProjectDiagram} /> View Relationships
                              </button>
                            )}

                            {/* Preview PDF - available for any status, no audit log */}
                            <button onClick={(e) => handlePreviewPDF(invoice, e)} disabled={isLoadingPreview}>
                              <FontAwesomeIcon icon={faFilePdf} /> {isLoadingPreview ? 'Loading...' : 'Preview PDF'}
                            </button>

                            {/* Download PDF - only for APPROVED invoices. The first
                                download is the Original; after that this button hands
                                out Copies, and says so. */}
                            {invoice.status === 'APPROVED' && (
                              <button
                                onClick={(e) => handleDownloadPDF(invoice, e)}
                                title={invoice.original_issued
                                  ? 'The original has already been downloaded. Copies are unlimited.'
                                  : 'Downloads the original. Every download after this one is a copy.'}
                              >
                                <FontAwesomeIcon icon={faDownload} />{' '}
                                {invoice.original_issued ? 'Copy Invoice' : 'Download PDF'}
                              </button>
                            )}
                            
                            {/* Only show Edit if NOT approved and not voided/cancelled */}
                            {invoice.status !== 'APPROVED' && !['VOIDED', 'CANCELLED'].includes(invoice.status) && (
                              <>
                                <button onClick={(e) => handleEdit(invoice, e)}>
                                  <FontAwesomeIcon icon={faEdit} /> Edit
                                </button>
                              </>
                            )}

                            {/* Audit log, record-payment, void and delete are all
                                outside the delivery-scoped whitelist — the last
                                three move or destroy money and additionally hold
                                their own HasFinancialAccess decorators. Hidden
                                for an operator, untouched for Finance. */}
                            {!scopedReader && (
                              <>
                                {/* Audit logs always available */}
                                <button onClick={(e) => handleViewInvoiceAudit(invoice, e)}>
                                  <FontAwesomeIcon icon={faClipboardList} /> View Audit Log
                                </button>

                                {/* Record Payment — live, unsettled invoices only */}
                                {PAYABLE_STATUSES.includes(invoice.status) && (
                                  <button onClick={(e) => handleRecordPayment(invoice, e)}>
                                    <FontAwesomeIcon icon={faMoneyBillWave} /> Record Payment
                                  </button>
                                )}

                                {/* Void — creates a reversing credit note; live invoices only */}
                                {VOIDABLE_STATUSES.includes(invoice.status) && (
                                  <>
                                    <hr />
                                    <button className="danger" onClick={(e) => handleVoid(invoice, e)}>
                                      <FontAwesomeIcon icon={faBan} /> Void Invoice
                                    </button>
                                  </>
                                )}

                                {/* Only show Delete if NOT approved and not voided/cancelled */}
                                {invoice.status !== 'APPROVED' && !['VOIDED', 'CANCELLED'].includes(invoice.status) && (
                                  <>
                                    <hr />
                                    <button className="danger" onClick={(e) => handleDelete(invoice, e)}>
                                      <FontAwesomeIcon icon={faTrash} /> Delete
                                    </button>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
        
        {/* Footer */}
        <Footer />
      </div>
      
      {/* Audit Modal */}
      <InvoiceAuditModal
        isOpen={showAuditModal}
        onClose={handleCloseAuditModal}
        invoiceType="outgoing"
        invoiceId={selectedInvoiceForAudit?.id || null}
        invoiceNumber={selectedInvoiceForAudit?.invoice_number || null}
      />
      
      {/* Relationship Modal */}
      <InvoiceRelationshipModal
        isOpen={showRelationshipModal}
        onClose={() => setShowRelationshipModal(false)}
        invoice={selectedInvoiceForRelationship}
        invoiceType="outgoing"
      />

      {/* PDF Preview Modal */}
      <PdfPreviewModal
        isOpen={showPdfPreview}
        pdfUrl={previewPdfUrl}
        fileName={previewFileName}
        onClose={handleClosePdfPreview}
      />

      <VoidInvoiceModal
        isOpen={showVoidModal}
        invoice={invoiceToVoid}
        noteType="credit"
        isProcessing={isVoiding}
        onConfirm={handleVoidConfirm}
        onCancel={handleVoidCancel}
      />

      {paymentInvoice && (
        <RecordPaymentModal
          invoice={paymentInvoice}
          recordPayment={invoicingService.recordOutgoingInvoicePayment}
          onRecorded={handlePaymentRecorded}
          onClose={() => setPaymentInvoice(null)}
        />
      )}

      {/* Payment Reminders lightbox — reuses this screen's own action handlers
          (minus Edit) so the dropdown behaves identically in both places */}
      <PaymentRemindersModal
        isOpen={showRemindersModal}
        onClose={() => setShowRemindersModal(false)}
        refreshToken={remindersRefreshToken}
        isLoadingPreview={isLoadingPreview}
        actions={{
          onView: handleView,
          onViewRelationships: handleViewRelationships,
          onPreviewPdf: handlePreviewPDF,
          onDownloadPdf: handleDownloadPDF,
          onViewAuditLog: handleViewInvoiceAudit,
          onVoid: handleVoid,
          onDelete: handleDelete,
        }}
      />
    </Layout>
  );
};

export default OutgoingInvoiceList;

